import { Input } from '@/components/ui/input';
import React, { ChangeEvent, HTMLInputTypeAttribute, KeyboardEventHandler, ReactNode } from 'react';
import { BaseIconButton } from '../Button';
import { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { BUTTONS_OVERWRITES } from '@/components/ui/_overwrites/buttons';

export type ButtonVariant = 'link' | 'outline' | 'default' | 'destructive' | 'secondary' | 'ghost' | null | undefined;
export type InputVariant = 'default' | 'outline' | 'ghost' | 'underline' | 'buttonWrapper';

const inputStyles = {
  default:
    'px-4 py-2 rounded-lg bg-gray-100 dark:bg-[#2f3844] border-2 border-gray-200 dark:border-[#52606d] text-sm dark:text-[#e1e8ed]',
  outline:
    'px-4 py-2 rounded-lg border-2 border-blue-500 dark:border-blue-400 bg-white dark:bg-[#52606d] text-sm dark:text-[#e1e8ed]',
  underline:
    'px-4 py-2 border-b-2 border-t-0 border-x-0 rounded-t-lg bg-white dark:bg-[#52606d] border-gray-200 focus:border-primary-500 dark:text-[#e1e8ed]',
  ghost: 'px-4 py-2 bg-transparent border-none text-sm dark:text-[#e1e8ed]',
  buttonWrapper: BUTTONS_OVERWRITES.classNames.variants.outline,
};

interface BaseInputProps {
  leftButton?: {
    element: ReactNode | LucideIcon;
    onClick?: () => void;
    variant?: ButtonVariant;
    classNames?: { button?: string; icon?: string };
  };
  rightButton?: {
    element: ReactNode | LucideIcon;
    onClick?: () => void;
    variant?: ButtonVariant;
    classNames?: { button?: string; icon?: string };
  };
  ref?: React.RefObject<HTMLInputElement>;
  variant?: InputVariant;
  placeholder?: string;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  type: HTMLInputTypeAttribute | undefined;
  value: any;
  dataTestid?: string;
}

const BaseInput = React.forwardRef<HTMLInputElement, BaseInputProps>(
  (
    {
      leftButton,
      rightButton,
      onChange,
      placeholder = 'Enter text here',
      type,
      value,
      variant = 'underline',
      onKeyDown,
      dataTestid,
      // className,
    },
    ref // Forwarded ref
  ) => {
    return (
      <div className="relative flex items-center" {...(dataTestid ? { 'data-testid': dataTestid } : {})}>
        {leftButton && (
          <div className="absolute left-1 top-1/2 transform -translate-y-1/2">
            <BaseIconButton
              variant="ghost"
              icon={leftButton?.element}
              onClick={leftButton?.onClick}
              classNames={leftButton?.classNames || { button: 'min-w-8 min-h-8' }}
            />
          </div>
        )}
        <Input
          ref={ref}
          type={type}
          onChange={onChange}
          onKeyDown={onKeyDown}
          value={value}
          placeholder={placeholder}
          className={cn(
            inputStyles[variant as InputVariant],
            `
            focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 
            ${leftButton ? 'pl-12' : ''} 
            ${rightButton ? 'pr-12' : ''}
            ${
              variant === 'buttonWrapper'
                ? 'p-3 text-base font-medium h-full border-none focus-visible:ring-2 focus-visible:ring-primary-500 active:ring-primary-500'
                : ''
            },
            `
          )}
        />
        {rightButton && (
          <div className="absolute right-1 top-1/2 transform -translate-y-1/2">
            <BaseIconButton
              variant="ghost"
              icon={rightButton?.element}
              onClick={rightButton?.onClick}
              classNames={rightButton?.classNames || { button: 'min-w-8 min-h-8' }}
            />
          </div>
        )}
      </div>
    );
  }
);

BaseInput.displayName = 'BaseInput';
export default BaseInput;
